import { ILiveMetric, IMetricResult } from 'fitbeat.models';
import { useEffect, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { getConfig } from '../config';
import { Logger } from './Logger';

const socketOptions = {
  forceNew: true,
  transports: ['websocket'],
};

interface IRealtimeMetrics {
  liveMetrics: IMetricResult | ILiveMetric;
}

const logPrefix = 'RealtimeMetricsWS';
let timer: null | Date = null;

const useRealtimeMetrics = (userId: string, heartRateMonitorId?: string): IRealtimeMetrics | null => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [realtimeMetrics, setMetrics] = useState<IRealtimeMetrics | null>(null);

  const setupEventListeners = (newSocket: Socket) => {
    newSocket.on('metrics-changed', (newMetrics: IRealtimeMetrics) => {
      if (timer) {
        const secondsElapsed = (Date.now() - timer.getTime()) / 1000;
        Logger.debug(`${logPrefix}: seconds from connection to first metrics: ${secondsElapsed}`, newMetrics);
        timer = null;
      }
      setMetrics(newMetrics);
    });

    newSocket.on('connect', () => {
      timer = new Date();
      Logger.debug(`${logPrefix}: ws connected`);
    });

    newSocket.on('error', (error: string) => {
      Logger.debug(`${logPrefix}: error: ${error}`);
    });

    newSocket.on('connect_error', (error: string) => {
      Logger.debug(`${logPrefix}: connection_error: ${error}`);
    });

    newSocket.on('reconnect', (attemptNumber: number) => {
      Logger.debug(`${logPrefix}: reconnect attempt number: ${attemptNumber}`);
    });

    newSocket.on('disconnect', (reason: string) => {
      Logger.debug(`${logPrefix}: disconnected reason: ${reason}`);
    });
  };

  const initializeWebSocketConnection = () => {
    const { hrMetricsServer } = getConfig();
    const newSocket = io(`${hrMetricsServer}/${userId}`, socketOptions);
    Logger.debug(`${logPrefix}: initializing websocket connection`);
    newSocket.connect();
    setupEventListeners(newSocket);
    setSocket(newSocket);
  };

  const closeSocketConnection = () => {
    socket?.off();
    socket?.close();
    setMetrics(null);
  };

  useEffect(() => {
    closeSocketConnection();
    initializeWebSocketConnection();
    return closeSocketConnection;
  }, [heartRateMonitorId]);

  return realtimeMetrics;
};

export default useRealtimeMetrics;
